import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";

import {ChoirState} from "./types";

import ChoirService from "./choir_services";
import {Choir} from "../../utils/types";

const getThunkErrorMessage = (action: any, fallback = 'Something went wrong') => {
    const payload = action?.payload;
    if (typeof payload === 'string' && payload.trim()) {
        return payload;
    }
    if (payload && typeof payload === 'object') {
        const message = payload?.message || payload?.data?.message;
        if (typeof message === 'string' && message.trim()) {
            return message;
        }
    }

    if (typeof action?.error?.message === 'string' && action.error.message.trim()) {
        return action.error.message;
    }

    return fallback;
};

// Create a thunk to create a choir
export const createChoir = createAsyncThunk(
    "choirs/createChoir",
    async (payload: Partial<Choir>, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.createChoir(payload);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data || error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to fetch choirs
export const fetchChoirs = createAsyncThunk(
    "choirs/fetchChoirs",
    async (query: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getChoirs(query);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to fetch a choir
export const fetchChoir = createAsyncThunk(
    "choirs/fetchChoir",
    async (id: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getChoir(id);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const updateChoir = createAsyncThunk(
    "choirs/updateChoir",
    async ({choir_id, payload}: {choir_id: string; payload: Partial<Choir>}, {rejectWithValue}): Promise<any> => {
        try {
            return await ChoirService.updateChoir(choir_id, payload);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data || error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to fetch choir public profile
export const fetchChoirPublicProfile = createAsyncThunk(
    "choirs/fetchChoirPublicProfile",
    async (uuid: string, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getChoirPublicProfile(uuid);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to fetch choir members
export const fetchChoirMembers = createAsyncThunk(
    "choirs/fetchChoirMembers",
    async (query: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getChoirMembers(query);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Join choir thunk
export const joinChoir = createAsyncThunk(
    "choirs/joinChoir",
    async (payload: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.joinChoir(payload);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Fetch join requests thunk
export const fetchJoinRequests = createAsyncThunk(
    "choirs/fetchJoinRequests",
    async (query: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getJoinRequests(query);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Fetch join request thunk
export const fetchJoinRequest = createAsyncThunk(
    "choirs/fetchJoinRequest",
    async (id: string, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getJoinRequest(id);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Accept join request thunk
export const acceptJoinRequest = createAsyncThunk(
    "choirs/acceptJoinRequest",
    async ({id, payload}: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.acceptJoinRequest(id, payload);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data || error?.response?.data?.message || error.message);
        }
    }
);

// Reject join request thunk
export const rejectJoinRequest = createAsyncThunk(
    "choirs/rejectJoinRequest",
    async ({id, payload}: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.rejectJoinRequest(id, payload);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to make member a moderator
export const makeMemberModerator = createAsyncThunk(
    "choirs/makeMemberModerator",
    async (payload: any, {rejectWithValue}): Promise<any> => {
        try {
            const {choir_id, user_email} = payload;
            // Make API request
            // Return response
            return await ChoirService.makeMemberModerator(choir_id, user_email);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const updateChoirMemberGroup = createAsyncThunk(
    "choirs/updateChoirMemberGroup",
    async (payload: any, {rejectWithValue}): Promise<any> => {
        try {
            const {choir_id, user_email, group} = payload;
            return await ChoirService.updateMemberGroup(choir_id, user_email, group);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Create choir rehearsal thunk
export const createChoirRehearsal = createAsyncThunk(
    "choirs/createChoirRehearsal",
    async (payload: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.createChoirRehearsal(payload);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data || error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to fetch choir rehearsals
export const fetchChoirRehearsals = createAsyncThunk(
    "choirs/fetchChoirRehearsals",
    async (query: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getChoirRehearsals(query);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to fetch choir rehearsals
export const fetchChoirRehearsalSongs = createAsyncThunk(
    "choirs/fetchChoirRehearsalSongs",
    async (query: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getChoirRehearsalSongs(query);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to fetch choir rehearsal documents
export const fetchChoirRehearsalDocuments = createAsyncThunk(
    "choirs/fetchChoirRehearsalDocuments",
    async (query: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getChoirRehearsalDocuments(query);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to fetch a choir rehearsal
export const fetchChoirRehearsal = createAsyncThunk(
    "choirs/fetchChoirRehearsal",
    async (data: any, {rejectWithValue}): Promise<any> => {
        try {
            const {choir_id, id} = data;
            // Make API request
            // Return response
            return await ChoirService.getChoirRehearsal(choir_id, id);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const deleteChoirRehearsal = createAsyncThunk(
    "choirs/deleteChoirRehearsal",
    async ({rehearsal_id, choir_id}: {rehearsal_id: string; choir_id: string}, {rejectWithValue}): Promise<any> => {
        try {
            return await ChoirService.deleteChoirRehearsal(rehearsal_id, choir_id);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const deleteChoirRehearsalSong = createAsyncThunk(
    "choirs/deleteChoirRehearsalSong",
    async ({rehearsal_id, song_uuid, choir_id, song_occurrence_uuid}: {
        rehearsal_id: string;
        song_uuid: string;
        choir_id: string;
        song_occurrence_uuid?: string;
    }, {rejectWithValue}): Promise<any> => {
        try {
            return await ChoirService.deleteChoirRehearsalSong(rehearsal_id, song_uuid, choir_id, song_occurrence_uuid);
        } catch (error: any) {
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to fetch choir practises
export const fetchUserChoirPractise = createAsyncThunk(
    "choirs/fetchUserChoirPractise",
    async (query: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getUserChoirPractise(query.rehearsal_id, query.song_id);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to fetch members choir practises
export const fetchMembersChoirPractise = createAsyncThunk(
    "choirs/fetchMembersChoirPractise",
    async (query: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getMembersChoirPractise(query.rehearsal_id, query.song_id, query.choir_id);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

// Create a thunk to fetch a member choir practise
export const fetchMemberChoirPractise = createAsyncThunk(
    "choirs/fetchMemberChoirPractise",
    async (query: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getMemberChoirPractise(query.rehearsal_id, query.song_id, query.user_email, query.choir_id);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchChoirSchedules = createAsyncThunk(
    "choirs/fetchChoirSchedules",
    async (query: any, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getChoirSchedules(query);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchServiceTypes = createAsyncThunk(
    "choirs/fetchServiceTypes",
    async (choir_id: number, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getServiceTypes(choir_id);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);

export const fetchExistingServiceDays = createAsyncThunk(
    "choirs/fetchExistingServiceDays",
    async (choir_id: number, {rejectWithValue}): Promise<any> => {
        try {
            // Make API request
            // Return response
            return await ChoirService.getExistingServiceDays(choir_id as any);
        } catch (error: any) {
            // If server error, throw error
            return rejectWithValue(error?.response?.data?.message || error.message);
        }
    }
);


// Create initial state
const initialState: ChoirState = {
    choirs: [],
    choir: null,
    members: [],
    member: null,
    choir_rehearsals: [],
    choir_schedules: null,
    choir_service_days: [],
    service_types: [],
    choir_rehearsal_songs: [],
    choir_rehearsal_documents: [],
    choir_rehearsal: null,
    choir_practise: null,
    members_practise: [],
    member_practise: null,
    join_requests: [],
    join_request: null,
    loading: false,
    error: null
}

// Create a slice
const choirSlice = createSlice({
    name: "choir",
    initialState,
    reducers: {
        clearNotification: (state) => {
            state.choir = null;
        },
        updateChoirPractise: (state, action) => {
            const {choir_practise} = action.payload;
            state.choir_practise = choir_practise;
        }
    },
    extraReducers: (builder) => {
        // Add reducers for createChoir
        builder
            .addCase(createChoir.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createChoir.fulfilled, (state, action) => {
                state.loading = false;
                state.choir = action.payload;
            })
            .addCase(createChoir.rejected, (state, action) => {
                state.loading = false;
                state.error = getThunkErrorMessage(action);
            })

        // Add reducers for fetchChoirs
        builder
            .addCase(fetchChoirs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchChoirs.fulfilled, (state, action) => {
                state.loading = false;
                state.choirs = action.payload;
                // state.choir = null; // TODO: Review if it causes further bugs. Currently doing this because of the way useDynamicBreadcrumb is being implemented for the choir detail page
            })
            .addCase(fetchChoirs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchChoir
        builder
            .addCase(fetchChoir.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchChoir.fulfilled, (state, action) => {
                state.loading = false;
                state.choir = action.payload;
            })
            .addCase(fetchChoir.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        builder
            .addCase(updateChoir.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateChoir.fulfilled, (state, action) => {
                state.loading = false;
                state.choir = action.payload;
            })
            .addCase(updateChoir.rejected, (state, action) => {
                state.loading = false;
                state.error = getThunkErrorMessage(action);
            })

        // Add reducers for fetchChoirPublicProfile
        builder
            .addCase(fetchChoirPublicProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchChoirPublicProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.choir = action.payload;
            })
            .addCase(fetchChoirPublicProfile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchChoirMembers
        builder
            .addCase(fetchChoirMembers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchChoirMembers.fulfilled, (state, action) => {
                state.loading = false;
                state.members = action.payload;
            })
            .addCase(fetchChoirMembers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for joinChoir
        builder
            .addCase(joinChoir.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(joinChoir.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(joinChoir.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchJoinRequests
        builder
            .addCase(fetchJoinRequests.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchJoinRequests.fulfilled, (state, action) => {
                state.loading = false;
                state.join_requests = action.payload;
            })
            .addCase(fetchJoinRequests.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchJoinRequest
        builder
            .addCase(fetchJoinRequest.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchJoinRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.join_request = action.payload;
            })
            .addCase(fetchJoinRequest.rejected, (state, action) => {
                state.loading = false;
                state.join_request = null;
                state.error = action.payload as string;
            })

        // Add reducers for updateJoinRequest
        builder
            .addCase(acceptJoinRequest.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(acceptJoinRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.join_requests = state.join_requests.filter((request) => request.user_email !== action.payload.user_email);
            })
            .addCase(acceptJoinRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for rejectJoinRequest
        builder
            .addCase(rejectJoinRequest.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(rejectJoinRequest.fulfilled, (state, action) => {
                state.loading = false;
                state.join_requests = state.join_requests.filter((request) => request.user_email !== action.payload.user_email);
            })
            .addCase(rejectJoinRequest.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for createChoirRehearsal
        builder
            .addCase(createChoirRehearsal.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createChoirRehearsal.fulfilled, (state, action) => {
                state.loading = false;
                state.choir_rehearsals = action.payload;
            })
            .addCase(createChoirRehearsal.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchChoirRehearsals
        builder
            .addCase(fetchChoirRehearsals.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchChoirRehearsals.fulfilled, (state, action) => {
                state.loading = false;
                state.choir_rehearsals = action.payload;
            })
            .addCase(fetchChoirRehearsals.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchChoirRehearsalSongs
        builder
            .addCase(fetchChoirRehearsalSongs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchChoirRehearsalSongs.fulfilled, (state, action) => {
                state.loading = false;
                state.choir_rehearsal_songs = action.payload;
            })
            .addCase(fetchChoirRehearsalSongs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchChoirRehearsalDocuments
        builder
            .addCase(fetchChoirRehearsalDocuments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchChoirRehearsalDocuments.fulfilled, (state, action) => {
                state.loading = false;
                state.choir_rehearsal_documents = action.payload;
            })
            .addCase(fetchChoirRehearsalDocuments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchChoirRehearsal
        builder
            .addCase(fetchChoirRehearsal.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchChoirRehearsal.fulfilled, (state, action) => {
                state.loading = false;
                state.choir_rehearsal = action.payload;
            })
            .addCase(fetchChoirRehearsal.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        builder
            .addCase(deleteChoirRehearsal.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteChoirRehearsal.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(deleteChoirRehearsal.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        builder
            .addCase(deleteChoirRehearsalSong.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteChoirRehearsalSong.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(deleteChoirRehearsalSong.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchUserChoirPractise
        builder
            .addCase(fetchUserChoirPractise.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserChoirPractise.fulfilled, (state, action) => {
                state.loading = false;
                state.choir_practise = action.payload;
            })
            .addCase(fetchUserChoirPractise.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchMembersChoirPractise
        builder
            .addCase(fetchMembersChoirPractise.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMembersChoirPractise.fulfilled, (state, action) => {
                state.loading = false;
                state.members_practise = action.payload;
            })
            .addCase(fetchMembersChoirPractise.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchMemberChoirPractise
        builder
            .addCase(fetchMemberChoirPractise.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMemberChoirPractise.fulfilled, (state, action) => {
                state.loading = false;
                state.member_practise = action.payload;
            })
            .addCase(fetchMemberChoirPractise.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchChoirSchedules
        builder
            .addCase(fetchChoirSchedules.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchChoirSchedules.fulfilled, (state, action) => {
                state.loading = false;
                state.choir_schedules = action.payload;
            })
            .addCase(fetchChoirSchedules.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchServiceTypes
        builder
            .addCase(fetchServiceTypes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchServiceTypes.fulfilled, (state, action) => {
                state.loading = false;
                state.service_types = action.payload;
            })
            .addCase(fetchServiceTypes.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

        // Add reducers for fetchExistingServiceDays
        builder
            .addCase(fetchExistingServiceDays.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchExistingServiceDays.fulfilled, (state, action) => {
                state.loading = false;
                state.choir_service_days = action.payload;
            })
            .addCase(fetchExistingServiceDays.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

    }
});


// Export reducer
export default choirSlice.reducer;
// Export actions
export const {clearNotification, updateChoirPractise} = choirSlice.actions;
